import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Alert,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';

// ── TFSA annual contribution limits (2009–2026) ──
const TFSA_LIMITS = {
  2009: 5000, 2010: 5000, 2011: 5000, 2012: 5000,
  2013: 5500, 2014: 5500, 2015: 10000, 2016: 5500,
  2017: 5500, 2018: 5500, 2019: 6000, 2020: 6000,
  2021: 6000, 2022: 6000, 2023: 6500, 2024: 7000,
  2025: 7000, 2026: 7000,
};

// ── Combined federal + provincial marginal rates (simplified brackets) ──
const COMBINED_MARGINAL_RATE = {
  QC: [
    { max: 50000, rate: 0.275 },
    { max: 55000, rate: 0.325 },
    { max: 100000, rate: 0.375 },
    { max: 150000, rate: 0.44 },
    { max: Infinity, rate: 0.50 },
  ],
  ON: [
    { max: 50000, rate: 0.20 },
    { max: 55000, rate: 0.295 },
    { max: 100000, rate: 0.315 },
    { max: 150000, rate: 0.38 },
    { max: Infinity, rate: 0.46 },
  ],
  BC: [
    { max: 45000, rate: 0.205 },
    { max: 55000, rate: 0.285 },
    { max: 100000, rate: 0.315 },
    { max: 150000, rate: 0.38 },
    { max: Infinity, rate: 0.45 },
  ],
  AB: [
    { max: 55000, rate: 0.25 },
    { max: 100000, rate: 0.305 },
    { max: 150000, rate: 0.36 },
    { max: Infinity, rate: 0.44 },
  ],
  OTHER: [
    { max: 50000, rate: 0.24 },
    { max: 55000, rate: 0.30 },
    { max: 100000, rate: 0.33 },
    { max: 150000, rate: 0.39 },
    { max: Infinity, rate: 0.46 },
  ],
};

const PROVINCES = [
  { value: 'ON', label: 'Ontario' },
  { value: 'QC', label: 'Quebec' },
  { value: 'BC', label: 'Columbia Británica' },
  { value: 'AB', label: 'Alberta' },
  { value: 'OTHER', label: 'Otra provincia' },
];

// ── Pure functions ──

function calcContributionRoom(arrivalYear) {
  const startYear = Math.max(Number(arrivalYear), 2009);
  let total = 0;
  for (let y = startYear; y <= 2026; y++) {
    total += TFSA_LIMITS[y] || 0;
  }
  return total;
}

function getMarginalRate(province, income) {
  const brackets = COMBINED_MARGINAL_RATE[province] || COMBINED_MARGINAL_RATE.OTHER;
  for (const bracket of brackets) {
    if (income <= bracket.max) return bracket.rate;
  }
  return brackets[brackets.length - 1].rate;
}

function buildRecommendation(income, province, goal, rate, room) {
  // Priority logic
  let priority; // 'tfsa' | 'both' | 'rrsp'
  let explanation;

  if (province === 'QC' && rate >= 0.27) {
    // Quebec override: higher provincial taxes make RRSP attractive at lower brackets
    if (rate < 0.35) {
      priority = 'both';
      explanation =
        'En Quebec, la tasa combinada es más alta que en otras provincias. Aunque tu ingreso es moderado, el RRSP te da un beneficio fiscal significativo aquí. Usa ambos: TFSA para flexibilidad y RRSP para maximizar la devolución de impuestos.';
    } else {
      priority = 'rrsp';
      explanation =
        'Con tu nivel de ingreso en Quebec, la deducción del RRSP te genera un ahorro fiscal importante - cada $1,000 depositados te ahorran ~$' +
        Math.round(rate * 1000).toLocaleString() +
        ' en impuestos. Prioriza el RRSP y usa la TFSA para el excedente.';
    }
  } else if (rate < 0.30) {
    priority = 'tfsa';
    explanation =
      'Con tu nivel de ingreso, la deducción fiscal del RRSP es relativamente pequeña. La TFSA te conviene más porque tus ganancias crecen libres de impuestos para siempre, y puedes retirar sin penalización.';
  } else if (rate <= 0.40) {
    priority = 'both';
    explanation =
      'Estás en un bracket medio donde ambas cuentas son valiosas. Usa el RRSP para reducir impuestos ahora (ahorro de ~$' +
      Math.round(rate * 1000).toLocaleString() +
      ' por cada $1,000) y la TFSA para metas flexibles y retiro libre de impuestos.';
  } else {
    priority = 'rrsp';
    explanation =
      'Tu tasa marginal es alta - cada $1,000 en el RRSP te ahorra ~$' +
      Math.round(rate * 1000).toLocaleString() +
      ' en impuestos. Maximiza el RRSP primero, luego contribuye lo que puedas a la TFSA.';
  }

  // Goal-specific product recommendation
  let productRec;
  switch (goal) {
    case 'emergencia':
      productRec =
        'Para un fondo de emergencia, usa una TFSA con cuenta de ahorro de alto interés (HISA). Busca tasas de 4%+ en EQ Bank, Tangerine o Wealthsimple. Tu dinero estará disponible en 1-2 días.';
      break;
    case 'largo-plazo':
      productRec =
        'Para crecimiento a largo plazo (+5 años), considera ETFs diversificados dentro de tu TFSA. Opciones populares: XEQT (100% acciones globales) o VBAL (60/40 acciones/bonos). Plataformas: Wealthsimple o Questrade.';
      break;
    default: // 'general'
      productRec =
        'Para ahorro general, combina: una parte en HISA (fondo de emergencia, 3-6 meses de gastos) y otra en ETFs de bajo costo (XEQT o VEQT) para crecimiento. Ambos pueden estar dentro de tu TFSA.';
  }

  const monthlyTarget = Math.round(room / 12);

  return { priority, explanation, productRec, monthlyTarget };
}

// ── Years for Select ──
const YEARS = [];
for (let y = 2009; y <= 2026; y++) YEARS.push(y);

// ── Component ──

const TFSACalculator = () => {
  const [yearArrived, setYearArrived] = useState('2024');
  const [province, setProvince] = useState('ON');
  const [annualIncome, setAnnualIncome] = useState('');
  const [financialGoal, setFinancialGoal] = useState('emergencia');
  const [result, setResult] = useState(null);

  const contributionRoom = useMemo(
    () => calcContributionRoom(yearArrived),
    [yearArrived],
  );

  useEffect(() => {
    const income = Number(annualIncome);
    if (!income || income <= 0) {
      setResult(null);
      return;
    }
    const rate = getMarginalRate(province, income);
    const room = contributionRoom;
    const rec = buildRecommendation(income, province, financialGoal, rate, room);
    setResult({ ...rec, rate, room });
  }, [annualIncome, province, financialGoal, contributionRoom]);

  const handleGoal = (_, val) => {
    if (val !== null) setFinancialGoal(val);
  };

  return (
    <Box>
      {/* ── Inputs ── */}
      <Grid container spacing={2.5}>
        {/* Year arrived */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Año de llegada a Canadá</InputLabel>
            <Select
              value={yearArrived}
              label="Año de llegada a Canadá"
              onChange={(e) => setYearArrived(e.target.value)}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Province */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Provincia</InputLabel>
            <Select
              value={province}
              label="Provincia"
              onChange={(e) => setProvince(e.target.value)}
            >
              {PROVINCES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Annual income */}
        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            fullWidth
            size="small"
            label="Ingreso anual bruto"
            prefix="$"
            thousandSeparator=","
            decimalScale={0}
            allowNegative={false}
            value={annualIncome}
            onValueChange={(vals) => setAnnualIncome(vals.value)}
            placeholder="Ej. $55,000"
          />
        </Grid>

        {/* Financial goal */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            sx={{ color: '#1A1A1A', mb: 0.75, fontWeight: 500 }}
          >
            Meta financiera
          </Typography>
          <ToggleButtonGroup
            value={financialGoal}
            exclusive
            onChange={handleGoal}
            size="small"
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8125rem',
                borderColor: '#E6E9EE',
                color: '#1A1A1A',
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#1E3A5F',
                  color: '#fff',
                  borderColor: '#1E3A5F',
                  '&:hover': { bgcolor: '#1A3152' },
                },
              },
            }}
          >
            <ToggleButton value="emergencia">Emergencia</ToggleButton>
            <ToggleButton value="general">Ahorro general</ToggleButton>
            <ToggleButton value="largo-plazo">Largo plazo</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {/* ── Output ── */}
      <Divider sx={{ my: 3 }} />

      {/* Contribution room - always visible */}
      <Box
        sx={{
          borderLeft: '4px solid #1E3A5F',
          bgcolor: '#F7F9FC',
          borderRadius: '0 8px 8px 0',
          p: { xs: 2.5, md: '32px' },
          mb: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: '#5A6570', mb: 0.5 }}>
          Tu espacio de contribución TFSA estimado
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 36 },
            fontWeight: 700,
            color: '#1E3A5F',
            lineHeight: 1.2,
          }}
        >
          ${contributionRoom.toLocaleString()} CAD
        </Typography>
        <Typography variant="body2" sx={{ color: '#5A6570', mt: 0.5 }}>
          {'Meta mensual para llenar tu TFSA: '}
          <strong>${Math.round(contributionRoom / 12).toLocaleString()}/mes</strong>
        </Typography>
      </Box>

      {/* Full recommendation - only when income is entered */}
      {result ? (
        <Box
          sx={{
            borderLeft: '4px solid #1E3A5F',
            bgcolor: '#F7F9FC',
            borderRadius: '0 10px 10px 0',
            p: { xs: 2.5, md: '32px' },
          }}
        >
          {/* Section heading */}
          <Typography
            variant="overline"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: '#1E3A5F',
              letterSpacing: 1.4,
              display: 'block',
              mb: 2.5,
            }}
          >
            RECOMENDACI&Oacute;N PERSONALIZADA
          </Typography>

          {/* TFSA vs RRSP priority */}
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '8px',
              border: '1px solid #E6E9EE',
              p: { xs: 2, md: 3 },
              mb: 2.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}
            >
              {result.priority === 'tfsa' && '✦ Prioridad: TFSA primero'}
              {result.priority === 'both' && '✦ Estrategia: TFSA + RRSP'}
              {result.priority === 'rrsp' && '✦ Prioridad: RRSP primero'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#1A1A1A', lineHeight: 1.7 }}
            >
              {result.explanation}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#5A6570', mt: 1.5 }}
            >
              {'Tu tasa marginal estimada: '}
              <strong>{(result.rate * 100).toFixed(1)}%</strong>
            </Typography>

            {province === 'QC' && (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  bgcolor: 'transparent',
                  border: '1px solid #90CAF9',
                  '& .MuiAlert-icon': { color: '#1E3A5F' },
                }}
              >
                {'En Quebec debes hacer '}<strong>dos declaraciones de impuestos</strong>
                {' (federal + provincial). Esto hace que la deducción del RRSP sea aún más valiosa porque reduces impuestos en ambas.'}
              </Alert>
            )}
          </Box>

          {/* Goal-specific product */}
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E6E9EE',
              borderRadius: '8px',
              p: { xs: 2, md: 3 },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}
            >
              Recomendación según tu meta
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#1A1A1A', lineHeight: 1.7 }}
            >
              {result.productRec}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography
          sx={{
            color: '#5F6B7A',
            fontStyle: 'italic',
            fontSize: 13,
            letterSpacing: 0.2,
            mt: 2,
          }}
        >
          Ingresa tu ingreso anual para ver una recomendación personalizada TFSA vs RRSP.
        </Typography>
      )}
    </Box>
  );
};

export default TFSACalculator;
